import React, { useState, useEffect, useRef } from 'react';
import { Search, X, User, Loader2, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Avatar } from '../ui/avatar';
import { Button } from '../ui/button';

interface UserSearchSelectProps {
  onSelect: (users: SelectedUser[]) => void;
  selectedUsers?: SelectedUser[];
  excludeUserIds?: string[];
  className?: string;
  placeholder?: string;
  maxUsers?: number;
}

export interface SelectedUser {
  id: string;
  auth_id: string;
  name: string;
  image?: string;
}

const UserSearchSelect: React.FC<UserSearchSelectProps> = ({
  onSelect,
  selectedUsers = [],
  excludeUserIds = [],
  className,
  placeholder = "Search members...",
  maxUsers = 10
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SelectedUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selected, setSelected] = useState<SelectedUser[]>(selectedUsers);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search for users
  useEffect(() => {
    const searchUsers = async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsLoading(true);
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get user's database ID
        const { data: currentUserData } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();

        if (!currentUserData) return;

        // Search for users
        const { data, error } = await supabase
          .from('users')
          .select('id, auth_id, full_name, avatar_url')
          .ilike('full_name', `%${searchTerm}%`)
          .neq('auth_id', user.id)
          .limit(10);

        if (error) throw error;

        // Filter out already selected users and excluded users
        const selectedIds = selected.map(u => u.auth_id);
        const filteredResults = data
          .filter(u => !selectedIds.includes(u.auth_id) && !excludeUserIds.includes(u.auth_id))
          .map(u => ({
            id: u.id,
            auth_id: u.auth_id,
            name: u.full_name,
            image: u.avatar_url
          }));

        setSearchResults(filteredResults);
      } catch (error) {
        console.error('Error searching users:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    const delaySearch = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(delaySearch);
  }, [searchTerm, selected, excludeUserIds]);

  const handleSelectUser = (user: SelectedUser) => {
    if (selected.length >= maxUsers) return;
    
    const newSelected = [...selected, user];
    setSelected(newSelected);
    onSelect(newSelected);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleRemoveUser = (userId: string) => {
    const newSelected = selected.filter(u => u.auth_id !== userId);
    setSelected(newSelected);
    onSelect(newSelected);
  };

  return (
    <div className={cn("w-full", className)} ref={containerRef}>
      {/* Selected users */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selected.map(user => (
            <div 
              key={user.auth_id}
              className="flex items-center gap-2 px-3 py-1.5 bg-dark-300 rounded-full"
            >
              <Avatar 
                src={user.image} 
                alt={user.name}
                size={20}
              />
              <span className="text-sm text-white">{user.name}</span>
              <button
                onClick={() => handleRemoveUser(user.auth_id)}
                className="text-dark-800 hover:text-white transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dark-800" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (e.target.value.length >= 2) {
              setShowResults(true);
            }
          }}
          onFocus={() => {
            if (searchTerm.length >= 2) {
              setShowResults(true);
            }
          }}
          placeholder={placeholder}
          className={cn(
            "w-full pl-10 pr-10 py-2 bg-dark-300 border border-dark-400 rounded-lg",
            "text-white placeholder-dark-800",
            "focus:outline-none focus:ring-1 focus:ring-gold-400/50 focus:border-gold-400/50"
          )}
          disabled={selected.length >= maxUsers}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-4 w-4 text-dark-800 animate-spin" />
          </div>
        )}
        {selected.length >= maxUsers && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <span className="text-xs text-dark-800">Max {maxUsers} users</span>
          </div>
        )}
      </div>

      {/* Search results dropdown */}
      {showResults && searchResults.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-dark-200 border border-dark-400 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {searchResults.map(user => (
            <button
              key={user.auth_id}
              onClick={() => {
                handleSelectUser(user);
                setShowResults(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-dark-300 transition-colors text-left"
            >
              <Avatar 
                src={user.image} 
                alt={user.name}
                size={32}
              />
              <span className="text-white">{user.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {showResults && searchTerm.length >= 2 && !isLoading && searchResults.length === 0 && (
        <div className="absolute z-10 mt-1 w-full bg-dark-200 border border-dark-400 rounded-lg shadow-lg p-4 text-center">
          <User className="h-6 w-6 text-dark-800 mx-auto mb-2" />
          <p className="text-dark-800">No members found</p>
        </div>
      )}
    </div>
  );
};

export default UserSearchSelect;